/**
 * 全站统一消息提示封装
 *
 * 分层责任（详见 docs/frontend/消息提示规范.md）：
 *   - store 层：静音，返回 { success, data?, error? }（既有约定，不变）
 *   - 页面/组件层：用本模块函数弹 toast
 *   - 网络/超时/5xx：由 httpClient 拦截器派发 system-error 事件，走 SystemAlertBanner 全局横幅，
 *     页面不要再对同类错误重复弹 toast
 *
 * 文案约定：
 *   - 成功 = 动词 + 结果：「保存成功」「已移入回收站」
 *   - 失败 = 动作 + 失败 + 具体原因：统一走 showErrorFrom(e, 'xx失败')，
 *     禁止写死笼统文案丢弃后端返回的 message
 *   - 必填/校验类提醒 = showWarning
 *
 * 新代码禁止直接 import { ElMessage } from 'element-plus'，统一使用本模块；
 * 存量调用按「改到哪个文件顺手迁」的策略渐进迁移。
 */
import { ElMessage } from 'element-plus'
import type { MessageOptions } from 'element-plus'
import { extractErrorMessage } from '@/utils/errorUtils'

/** 允许调用方覆盖的选项 */
type ShowOptions = Partial<Pick<MessageOptions, 'duration' | 'grouping' | 'showClose' | 'offset'>>

/** 公共默认：合并重复文案；限制同屏最大条数防刷屏 */
const BASE = { grouping: true, max: 5 } as const

/** 各类型默认时长：成功短促；error/warning 常驻更久且可手动关闭 */
const DEFAULTS = {
  success: { duration: 2000 },
  info: { duration: 3000 },
  warning: { duration: 4000, showClose: true },
  error: { duration: 5000, showClose: true },
} as const

type MessageType = keyof typeof DEFAULTS

function show(type: MessageType, message: string, options?: ShowOptions) {
  return ElMessage({ type, message, ...BASE, ...DEFAULTS[type], ...options })
}

/** 成功提示：动词 + 结果 */
export function showSuccess(message: string, options?: ShowOptions) {
  return show('success', message, options)
}

/** 警告提示：必填/校验类提醒、部分成功的诚实汇总 */
export function showWarning(message: string, options?: ShowOptions) {
  return show('warning', message, options)
}

/** 信息提示 */
export function showInfo(message: string, options?: ShowOptions) {
  return show('info', message, options)
}

/** 失败提示：调用方已持有具体错误文案时使用 */
export function showError(message: string, options?: ShowOptions) {
  return show('error', message, options)
}

/**
 * 失败提示（推荐）：从 unknown 错误中提取用户友好的文案后弹出。
 * 提取规则复用 extractErrorMessage（基于 classifyError）：
 *   - 业务 4xx（ApiError code 400-499）→ 透出后端返回的 message
 *   - 网络/超时/5xx → 转为友好文案（此类同时会触发全局横幅，页面无需重复处理）
 *   - 其余 → classifyError 的统一兜底文案
 *
 * 用法：catch (e) { showErrorFrom(e, '保存失败') }
 */
export function showErrorFrom(e: unknown, fallback: string, options?: ShowOptions) {
  return showError(extractErrorMessage(e, fallback), options)
}
